import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";

import logo from "assets/img/logo/logo.png";
import animatedDrone from "assets/img/3dmodel/animated_drone.glb";
import archOfLivingWood from "assets/img/3dmodel/arch_of_the__living__wood.glb";
import globeShowpiece from "assets/img/3dmodel/globe_showpiece.glb";
import furnitureDresser from "assets/img/3dmodel/furniture_dresser.glb";
import terracottaPottery from "assets/img/3dmodel/terracotta_pottery.glb";
import woodenBirdShowpieces from "assets/img/3dmodel/wooden_bird_showpieces.glb";
import decorativeFurniture from "assets/img/3dmodel/decorative_furniture.glb";
import sofaFurnitureSet from "assets/img/3dmodel/sofa_furniture_set..glb";
import someFurniture from "assets/img/3dmodel/some_furniture.glb";
import armChairFurniture from "assets/img/3dmodel/arm_chair__furniture.glb";

const SITE_URL = "https://www.rionick.com";

const products = [
  {
    id: "animated-drone",
    name: "PS5 Product Model",
    category: "Interactive 3D Product",
    description:
      "A live 3D product viewer with auto-rotation, zoom, drag inspection, AR support, and embeddable delivery from Rionick.",
    model: animatedDrone,
  },
  {
    id: "living-wood-arch",
    name: "Living Wood Arch",
    category: "Interactive 3D Product",
    description:
      "An inspectable wooden decor model prepared for product catalogs, interiors, and web embeds.",
    model: archOfLivingWood,
  },
  {
    id: "globe-showpiece",
    name: "Globe Showpiece",
    category: "Interactive 3D Product",
    description:
      "A decorative globe showpiece viewer with smooth rotation and product inspection controls.",
    model: globeShowpiece,
  },
  {
    id: "furniture-dresser",
    name: "Furniture Dresser",
    category: "Interactive 3D Product",
    description:
      "A web-ready furniture dresser model for product demos, catalogs, and client embeds.",
    model: furnitureDresser,
  },
  {
    id: "terracotta-pottery",
    name: "Terracotta Pottery",
    category: "Interactive 3D Product",
    description:
      "A terracotta pottery model viewer designed for handmade decor and lifestyle product showcases.",
    model: terracottaPottery,
  },
  {
    id: "wooden-bird-showpieces",
    name: "Wooden Bird Showpieces",
    category: "Interactive 3D Product",
    description:
      "A lightweight decorative showpiece model with light and dark viewer background options.",
    model: woodenBirdShowpieces,
  },
  {
    id: "decorative-furniture",
    name: "Decorative Furniture",
    category: "Interactive 3D Product",
    description:
      "Hosted decorative furniture visualization designed for fast sharing and simple implementation.",
    model: decorativeFurniture,
  },
  {
    id: "sofa-furniture-set",
    name: "Sofa Furniture Set",
    category: "Interactive 3D Product",
    description:
      "A client-ready sofa furniture set viewer with Rionick-hosted model delivery and controls.",
    model: sofaFurnitureSet,
  },
  {
    id: "some-furniture",
    name: "Furniture Model",
    category: "Interactive 3D Product",
    description:
      "A furniture model viewer for quick inspection, rotation, and embed-ready product pages.",
    model: someFurniture,
  },
  {
    id: "arm-chair-furniture",
    name: "Arm Chair Furniture",
    category: "Interactive 3D Product",
    description:
      "An arm chair furniture viewer with drag inspection and copy-ready client embed code.",
    model: armChairFurniture,
  },
];

const getInitialTheme = () => {
  if (typeof window === "undefined") return "dark";

  const theme = new URLSearchParams(window.location.search).get("theme");
  return theme === "light" ? "light" : "dark";
};

const loadModelViewerScript = () => {
  if (customElements.get("model-viewer")) return;
  if (document.querySelector('script[data-rionick-model-viewer="true"]')) return;

  const script = document.createElement("script");
  script.type = "module";
  script.src = "https://unpkg.com/@google/model-viewer/dist/model-viewer.min.js";
  script.dataset.rionickModelViewer = "true";
  document.head.appendChild(script);
};

const getEmbedUrl = (productId, theme = "dark") =>
  `${SITE_URL}/3D_Products/embed/${productId}?theme=${theme}`;

const getEmbedCode = (product, theme = "dark") => `<iframe
  src="${getEmbedUrl(product.id, theme)}"
  title="Rionick 3D Viewer - ${product.name}"
  width="100%"
  height="560"
  style="border:0;border-radius:20px;overflow:hidden;"
  allow="fullscreen; xr-spatial-tracking"
  loading="lazy">
</iframe>`;

const ProductViewer = ({ product, compact = false, theme = "dark", variant = "default" }) => {
  const shellRef = useRef(null);
  const viewerRef = useRef(null);

  const frameModelToBounds = useCallback((viewer) => {
    if (!viewer?.getBoundingBoxCenter || !viewer?.getDimensions) return;

    const viewerRect = viewer.getBoundingClientRect();
    if (!viewerRect.width || !viewerRect.height) return;

    const center = viewer.getBoundingBoxCenter();
    const dimensions = viewer.getDimensions();
    const maxDimension = Math.max(dimensions.x, dimensions.y, dimensions.z);

    if (!Number.isFinite(maxDimension) || maxDimension <= 0) return;

    const cameraDistance = Math.max(maxDimension * (compact ? 3.1 : 2.75), 0.35);
    const minDistance = Math.max(maxDimension * 0.65, 0.18);
    const maxDistance = Math.max(maxDimension * 8, 1.4);

    const cameraTarget = `${center.x}m ${center.y}m ${center.z}m`;
    viewer.style.width = "100%";
    viewer.style.height = "100%";
    viewer.setAttribute("camera-target", cameraTarget);
    viewer.setAttribute("camera-orbit", `28deg 68deg ${cameraDistance}m`);
    viewer.setAttribute("min-camera-orbit", `auto auto ${minDistance}m`);
    viewer.setAttribute("max-camera-orbit", `auto auto ${maxDistance}m`);
    viewer.setAttribute("field-of-view", compact ? "32deg" : "28deg");

    const centerModel = () => {
      viewer.style.width = "100%";
      viewer.style.height = "100%";
      viewer.setAttribute("camera-target", cameraTarget);
      viewer.setAttribute("camera-orbit", `28deg 68deg ${cameraDistance}m`);
      viewer.updateFraming?.();
      viewer.jumpCameraToGoal?.();
    };

    window.requestAnimationFrame(centerModel);
    window.setTimeout(centerModel, 80);
    window.setTimeout(centerModel, 260);
  }, [compact]);

  useEffect(() => {
    loadModelViewerScript();
  }, []);

  useEffect(() => {
    const viewer = viewerRef.current;
    const shell = shellRef.current;
    if (!viewer) return undefined;

    const handleLoad = async () => {
      if (viewer.updateComplete) {
        await viewer.updateComplete;
      }
      frameModelToBounds(viewer);
    };
    const handleResize = () => frameModelToBounds(viewer);
    viewer.addEventListener("load", handleLoad);
    window.addEventListener("resize", handleResize);

    const observer =
      shell && "ResizeObserver" in window
        ? new ResizeObserver(() => frameModelToBounds(viewer))
        : null;

    observer?.observe(shell);

    if (viewer.loaded) handleLoad();

    return () => {
      viewer.removeEventListener("load", handleLoad);
      window.removeEventListener("resize", handleResize);
      observer?.disconnect();
    };
  }, [frameModelToBounds]);

  return (
    <div
      ref={shellRef}
      className={`three-d-products__viewer ${compact ? "is-compact" : ""} is-${theme} is-${variant}`}
    >
      <model-viewer
        ref={viewerRef}
        style={{ width: "100%", height: "100%" }}
        className="three-d-products__model"
        src={product.model}
        alt={`${product.name} 3D model`}
        camera-controls
        bounds="tight"
        auto-rotate
        auto-rotate-delay="0"
        rotation-per-second="12deg"
        interaction-prompt="none"
        shadow-intensity="1.2"
        shadow-softness="0.75"
        exposure="1.1"
        environment-image={theme === "light" ? "neutral" : "legacy"}
        camera-orbit="35deg 64deg auto"
        field-of-view="30deg"
        ar
      >
        <div className="three-d-products__loading" slot="poster">
          Loading 3D viewer
        </div>
      </model-viewer>
    </div>
  );
};

const CopyIcon = () => (
  <svg
    aria-hidden="true"
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M8 8.75C8 7.784 8.784 7 9.75 7H18.25C19.216 7 20 7.784 20 8.75V18.25C20 19.216 19.216 20 18.25 20H9.75C8.784 20 8 19.216 8 18.25V8.75Z"
      stroke="currentColor"
      strokeWidth="1.8"
    />
    <path
      d="M5.75 16.5C4.784 16.5 4 15.716 4 14.75V5.75C4 4.784 4.784 4 5.75 4H14.25C15.216 4 16 4.784 16 5.75"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
    />
  </svg>
);

const ThreeDProducts = () => {
  const { productId } = useParams();
  const [copiedProduct, setCopiedProduct] = useState("");
  const [viewerTheme, setViewerTheme] = useState(getInitialTheme);
  const isEmbed = Boolean(productId);
  const selectedProduct = useMemo(
    () => products.find((item) => item.id === productId) || products[0],
    [productId]
  );

  useEffect(() => {
    document.title = isEmbed
      ? `${selectedProduct.name} | Rionick 3D Viewer`
      : "3D Products | Rionick Studios";
  }, [isEmbed, selectedProduct.name]);

  const copyEmbedCode = async (product) => {
    const code = getEmbedCode(product, viewerTheme);

    try {
      await navigator.clipboard.writeText(code);
      setCopiedProduct(product.id);
      window.setTimeout(() => setCopiedProduct(""), 1800);
    } catch (error) {
      const textarea = document.createElement("textarea");
      textarea.value = code;
      textarea.setAttribute("readonly", "");
      textarea.style.position = "fixed";
      textarea.style.left = "-9999px";
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand("copy");
      document.body.removeChild(textarea);
      setCopiedProduct(product.id);
      window.setTimeout(() => setCopiedProduct(""), 1800);
    }
  };

  if (isEmbed) {
    return (
      <main className={`three-d-products three-d-products--embed is-${viewerTheme}`}>
        <ProductViewer product={selectedProduct} compact theme={viewerTheme} variant="embed" />
        <div className="three-d-products__theme-toggle is-floating" aria-label="Viewer background theme">
          <button
            type="button"
            className={viewerTheme === "dark" ? "active" : ""}
            onClick={() => setViewerTheme("dark")}
          >
            Dark
          </button>
          <button
            type="button"
            className={viewerTheme === "light" ? "active" : ""}
            onClick={() => setViewerTheme("light")}
          >
            Light
          </button>
        </div>
        <div className="three-d-products__embed-brand">
          <img src={logo} alt="Rionick Studios" />
          <span>{selectedProduct.name}</span>
        </div>
      </main>
    );
  }

  return (
    <main className={`three-d-products is-${viewerTheme}`}>
      <section className="three-d-products__hero">
        <div className="three-d-products__ambient" aria-hidden="true">
          <span></span>
          <span></span>
          <span></span>
        </div>
        <div className="container">
          <div className="three-d-products__topbar">
            <img src={logo} alt="Rionick Studios" />
            <div className="three-d-products__top-actions">
              <div className="three-d-products__theme-toggle" aria-label="Viewer background theme">
                <button
                  type="button"
                  className={viewerTheme === "dark" ? "active" : ""}
                  onClick={() => setViewerTheme("dark")}
                >
                  Dark
                </button>
                <button
                  type="button"
                  className={viewerTheme === "light" ? "active" : ""}
                  onClick={() => setViewerTheme("light")}
                >
                  Light
                </button>
              </div>
              <a href={SITE_URL}>Back to website</a>
            </div>
          </div>
          <div className="row align-items-center gy-5">
            <div className="col-xl-6 col-lg-6">
              <span className="three-d-products__label">Rionick 3D Embed Library</span>
              <h1>Share interactive 3D product viewers with one copy-paste code.</h1>
              <p>
                Use this private collection page to preview product models and
                copy iframe embed codes for client websites. The model runs from
                Rionick, while the client only adds the snippet.
              </p>
            </div>
            <div className="col-xl-6 col-lg-6">
              <ProductViewer product={products[0]} theme={viewerTheme} variant="hero" />
            </div>
          </div>
        </div>
      </section>

      <section className="three-d-products__collection">
        <div className="container">
          <div className="three-d-products__section-title">
            <span>Available Models</span>
            <h2>3D Product Collection</h2>
            <p>
              Add future GLB files to the product list and each one will get its
              own viewer, embed URL, and copyable code.
            </p>
          </div>

          <div className="three-d-products__grid">
            {products.map((product) => (
              <article className="three-d-products__card" key={product.id}>
                <div className="three-d-products__card-viewer">
                  <ProductViewer product={product} compact theme={viewerTheme} variant="card" />
                </div>
                <div className="three-d-products__card-footer">
                  <h3>{product.name}</h3>
                  <button type="button" onClick={() => copyEmbedCode(product)}>
                    <CopyIcon />
                    {copiedProduct === product.id ? "Copied" : "Copy Embed Code"}
                  </button>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>
    </main>
  );
};

export default ThreeDProducts;
